import { NgService } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { ApiService } from "@app/services/api.service";
import { DialogButton, DialogButtonArgs, eAlertType, eInputDialogType, eMediaFilter, IAlert, IDialog, IDialogButton, IDialogOptions, IDialogService, IInputOptions, IMediaDialogOptions, IMediaObject, InputDialogButtonArgs, IRootScope, MediaDialogButtonArgs } from "@models";
import { extensions, icons } from "@tools";
import * as angular from "angular";


@NgService({
    token: Token.DialogService,
    dependencies: [
        Token.$RootScope,
        Token.$Compile,
        Token.$Timeout,
        Token.$Sanitize,
        Token.PromiseTracker,
        Token.ApiService,
    ]
})
export class DialogService implements IDialogService {
    constructor(
        private $rootScope: IRootScope,
        private $compile: ng.ICompileService,
        private $timeout: ng.ITimeoutService,
        private $sanitize: ng.sanitize.ISanitizeService,
        private promiseTracker: ng.IPromiseTracker,
        private api: ApiService
    ) {

        this.alerts = [];
        this.dialogs = [];
    }

    public alerts: IAlert[];
    public dialogs: IDialog[];

    public progress(options: IAlert) {
        if (!this.alerts) this.alerts = [];
        this.alerts.push(options);
    }

    public confirm(confirmMessage: string, yesCallback: () => void, noCallback?: () => void, okLabel?: string, cancelLabel?: string) {
        this.dialog({
            content: confirmMessage,
            buttons: [
                new DialogButton((okLabel ? okLabel : loc.ok), icons.success, yesCallback),
                new DialogButton((cancelLabel ? cancelLabel : loc.cancel), icons.cancel, noCallback)
            ]
        });
    }

    public alert(alert: IAlert) {
        if (!this.alerts) this.alerts = [];

        // handle the alert
        // build up the content (lives in msg div)
        if (alert.templateUrl || alert.template) {
            if (alert.templateUrl) {
                // if they provided a templateUrl, we'll use an ng-include to fetch it
                alert.msg = $(`<div class="kb-alertbar__msg" ng-include="'${alert.templateUrl}'"><span>Loading...</span></div>`);
            } else if (alert.template) {
                alert.msg = $(`<div class="kb-alertbar__msg">${alert.template}</div>`);
            }    
            // build a scope that we'll use to compile the content
            alert.scope = alert.scope || this.$rootScope.$new();
            // now compile the dialog with the scope
            this.$compile(alert.msg)(alert.scope);
        }
        // recommended way to handle digest to avoid async digest collisions
        this.$timeout(() => {
            this.alerts.push(alert);
        });
        
        // $apply scope to ensure first value of array is applied to ui
        //this.$rootScope.$apply(); -- caused digest collisions when digest in progress

        let timeShown = alert.timeShown || 5000;

        if (alert.promise) {
            (alert as any).inProgress = true;
            alert.promise.then(() => {
                alert.type = eAlertType.success;
                if (alert.successMsg) {
                    alert.msg = alert.successMsg;
                }
            }, reason => {
                alert.type = eAlertType.error;
                if (!angular.isDefined(alert.showPromiseReasonOnFail) || alert.showPromiseReasonOnFail) {
                    alert.msg = reason;
                }                
            }).finally(() => {
                (alert as any).inProgress = false;

                if (!alert.persist) {
                    this.$timeout(() => {
                        this.alerts.remove(alert);
                        if (alert.scope) alert.scope.$destroy();
                    }, timeShown);
                }
            });
        } else if (!alert.persist) {
            this.$timeout(() => {
                this.alerts.remove(alert);
                if (alert.scope) alert.scope.$destroy();
            }, timeShown);
        }

        return alert;
    }

    public closeAlert(index) {
        this.alerts.splice(index, 1);
    }

    public dialog(options: IDialogOptions) {
        let defaultOptions: IDialogOptions = {
            type: eAlertType.info,
            content: $("<div></div>"),
            templateUrl: null,
            template: null,
            buttons: [],
            buttonArgs: () => new DialogButtonArgs(),
            scope: null
        };

        options = angular.extend({}, defaultOptions, options);

        // build up the content (lives in msg div)
        if (options.templateUrl) {
            // if they provided a templateUrl, we'll use an ng-include to fetch it
            options.content = $(`<div class="kb-dialog__msg-content" ng-include="'${options.templateUrl}'"><span>Loading...</span></div>`);
        } else if (options.template) {
            options.content = $(options.template);
        } else if (angular.isString(options.content)) {
            options.content = $("<span>" + this.$sanitize(options.content) + "</span>");
        }

        // build a dialog object and add it to root scope
        let kbDialog: IDialog = {
            content: options.content,
            buttons: options.buttons,
            type: eAlertType[options.type],
            visible: false,
            keypress: e => {
                if (kbDialog.buttons && kbDialog.buttons.length) {
                    if (e.which == 13) {
                        (kbDialog.buttons[0] as any).click();
                    } else if (e.which == 27) { // escape
                        (kbDialog.buttons.last() as any).click();
                    }
                }
            },
            tracker: options.tracker,
            fullHeight: options.fullHeight
        };

        // set up a click command on each button that will call the callback with the args
        $.each(kbDialog.buttons, (index, button: IDialogButton) => {
            (button as any).click = () => {
                let args = options.buttonArgs();
                if (button.callback) {
                    button.callback(args);
                }
                if (args.close) {
                    kbDialog.visible = false;
                    // give time for animation to finish before removing
                    this.$timeout(() => {
                        this.dialogs.remove(kbDialog);
                        if (scope) scope.$destroy();
                    }, 200);
                }
            };
        });

        // build a scope that we'll use to compile the content
        let scope = options.scope || this.$rootScope.$new();
        // now compile the dialog with the scope
        this.$compile(kbDialog.content)(scope);

        // add the dialog to the rootscope
        if (!this.dialogs) this.dialogs = [];
        this.dialogs.push(kbDialog);

        // wait for shield animation
        this.$timeout(() => {
            this.$timeout(() => {
                kbDialog.visible = true;
            }, 10);
        }, 10);

        return kbDialog;
    }
    
    public input(options: IInputOptions) {

        let defaultOptions: IInputOptions = {
            msg: "",
            value: "",
            inputType: eInputDialogType.text,
            buttons: null,
            source: null,
            labelField: "",
            valueField: ""
        };
        options = angular.extend({}, defaultOptions, options);

        let scope: any = this.$rootScope.$new();
        scope.msg = options.msg;
        scope.value = options.value;
        scope.source = options.source;
        scope.labelField = options.labelField;
        scope.valueField = options.valueField;

        let input = "";
        switch (options.inputType) {
            case eInputDialogType.text:
                input = "<kb-textbox class='kb-dialog__input' ng-model='value'></kb-textbox>";
                break;
            case eInputDialogType.html:

                break;
            case eInputDialogType.textArea:
                input = "<kb-textarea class='kb-dialog__input' ng-model='value'></kb-textarea>";
                break;
            case eInputDialogType.select:
                input = "<kb-select class='kb-dialog__input' ng-model='value' source='source'" +
                    "          value-field='" + scope.valueField + "'" +
                    (scope.labelField ? "label-field='" + scope.labelField + "'" : "") + ">" +
                    "           {{item" + (scope.labelField ? "." + scope.labelField : "") + "}}" +
                    "       </kb-select>";
                break;
        }

        let dialogOptions: IDialogOptions = {
            type: eAlertType.info,
            content: $(
                "<div>{{msg}}</div>" +
                "<div class='kb-dialog__input-box'>" +
                input +
                "</div>"),
            templateUrl: null,
            buttons: options.buttons,
            buttonArgs: () => {
                let args = new InputDialogButtonArgs();
                args.value = scope.value;
                return args;
            },
            scope
        };

        return this.dialog(dialogOptions);
    }

    public mediaSelect(options) {
        let defaultOptions: IMediaDialogOptions = {
            startPath: "",
            buttons: null,
            filter: eMediaFilter.imagesAndVideos,
            apiPath: "api/media",
            rootDir: "media",
            open: (file: IMediaObject) => {
                window.open(file.id, "_blank");
            }
        };
        options = angular.extend({}, defaultOptions, options);
        let filter = "";

        switch (options.filter) {
            case eMediaFilter.images:
                filter = extensions.image;
                break;
            case eMediaFilter.videos:
                filter = extensions.video;
                break;
            case eMediaFilter.imagesAndVideos:
                filter = extensions.image + "," + extensions.video;
                break;
            case eMediaFilter.environments:
                filter = extensions.environments;
                break;
        }

        let scope: any = this.$rootScope.$new();
        scope.dStartPath = options.startPath;
        scope.tracker = this.promiseTracker();
        let apiPath = "api/media";
        if (options.apiPath) apiPath = options.apiPath;
        let rootDir = "media";
        if (options.rootDir) rootDir = options.rootDir;

        let dialogOptions: IDialogOptions = {
            type: eAlertType.info,
            content: $(
                '<kb-media-manager dialog="true" ' +
                '   multiple="false" ' +
                '   selected-file="dSelected" ' +
                '   start-path="{{dStartPath}}" ' +
                '   filter="' + filter + '"' +
                '   api-path="' + apiPath + '"' +
                '   root-dir="' + options.rootDir + '"' +
                '   tracker="tracker"' +
                '   ng-attr-style="{{\'text-align: left; height:\' + ($parent.windowHeight / 2) + \'px;\' }}">' +
                "</kb-media-manager>"
            ),
            templateUrl: null,
            buttons: options.buttons,
            buttonArgs: () => {
                let args = new MediaDialogButtonArgs();
                if (scope.dSelected) {
                    args.selectedItem = scope.dSelected;
                }
                return args;
            },
            scope,
            tracker: scope.tracker
        };

        return this.dialog(dialogOptions);
    }
    
}
